import type { Database } from '../../database.types';
import { getSupabaseClient, type DdmSupabaseClient } from '../../supabase';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type DealViewRow = Database['public']['Tables']['deal_views_history']['Row'];

type DealJoin = Pick<
  Database['public']['Tables']['deals']['Row'],
  | 'id'
  | 'title'
  | 'slug'
  | 'zone'
  | 'reference_id'
  | 'asking_price'
  | 'discount_pct'
  | 'dld_confidence'
  | 'dld_scope'
>;

export type DealViewRowJoined = DealViewRow & {
  deals: DealJoin | null;
};

// ---------------------------------------------------------------------------
// listViewHistory — read user's own consultation history (Spec §M7 line 1092)
// ---------------------------------------------------------------------------
// RLS: `deal_views_history_select` (20260506150000 lines 486-496) filters
// `user_id = auth.uid()` + `is_email_verified()`. CRON purge runs daily at
// 03:00 UTC removing rows older than 90 days (CRON `deal_views_history_purge_90d`).
//
// NOTE (ADR-025 §H): BO-6 V1 does NOT insert rows automatically when a deal
// page is mounted. The page therefore always returns an empty list until a
// future iteration wires `recordDealView()`. This is intentional and
// documented in ADR-025.

const SELECT_WITH_DEAL =
  '*, deals:deal_id (id, title, slug, zone, reference_id, asking_price, discount_pct, dld_confidence, dld_scope)';

const MAX_HISTORY_ROWS = 100;

export async function listViewHistory(
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<DealViewRowJoined[]> {
  const { data, error } = await client
    .from('deal_views_history')
    .select(SELECT_WITH_DEAL)
    .order('viewed_at', { ascending: false })
    .limit(MAX_HISTORY_ROWS);

  if (error) throw error;
  return (data ?? []) as unknown as DealViewRowJoined[];
}

// ---------------------------------------------------------------------------
// countDistinctDealsViewed7d — dashboard KPI "Views (7d)" (Spec §M7)
// ---------------------------------------------------------------------------
// Counts DISTINCT deals viewed in the last 7 days, NOT raw views: the dedup
// index is 1/user/deal/24h, so a deal viewed daily produces up to 7 rows — a
// plain row count would over-report. We fetch only the `deal_id` key column
// (bounded), not the deal payload, and de-dup client-side. RLS scopes the rows
// to the current buyer.

const VIEWS_7D_FETCH_CAP = 700; // 100 deals × 7 days ceiling — bounded, never a deal list.

export async function countDistinctDealsViewed7d(
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<number> {
  const sinceIso = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
  const { data, error } = await client
    .from('deal_views_history')
    .select('deal_id')
    .gte('viewed_at', sinceIso)
    .limit(VIEWS_7D_FETCH_CAP);
  if (error) throw error;
  return new Set((data ?? []).map((r) => r.deal_id)).size;
}

// ---------------------------------------------------------------------------
// clearViewHistory — DELETE all rows for the current user
// ---------------------------------------------------------------------------
// RLS: `deal_views_history_buyer_delete` (20260506150000 lines 500-506) only
// allows the buyer to wipe their own rows.

export async function clearViewHistory(
  userId: string,
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<void> {
  const { error } = await client.from('deal_views_history').delete().eq('user_id', userId);

  if (error) throw error;
}

// ---------------------------------------------------------------------------
// recordDealView — INSERT into deal_views_history (dedup: 1/user/deal/24h)
// ---------------------------------------------------------------------------
// The unique partial indexes on (user_id, deal_id) and (session_id, deal_id)
// with a 24h window ensure at most 1 row per viewer per deal per day. A
// duplicate INSERT raises 23505 (unique_violation) — we swallow it silently
// since the view was already counted.

const SESSION_KEY = 'ddm_session_id';

function getSessionId(): string {
  if (typeof window === 'undefined') return crypto.randomUUID();
  let id = sessionStorage.getItem(SESSION_KEY);
  if (!id) {
    id = crypto.randomUUID();
    sessionStorage.setItem(SESSION_KEY, id);
  }
  return id;
}

export async function recordDealView(
  dealId: string,
  userId: string | null,
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<void> {
  const row: Database['public']['Tables']['deal_views_history']['Insert'] = {
    deal_id: dealId,
    user_id: userId ?? undefined,
    session_id: userId ? undefined : getSessionId(),
  };

  const { error } = await client.from('deal_views_history').insert(row);

  if (error && error.code !== '23505') throw error;
}
